import { useCallback } from 'react';

import { SettingsProps } from './multiple-choice-settings.constants';
import { MultipleChoiceProperties } from '../multiple-choice.constants';
import { getOptionsPayload } from './multiple-choice-settings.utils';
import customNanoId from 'lib/nanoid/nanoid';
import {
  DRAFT_ACTION_COMPONENT_IMAGE_UPDATE,
  DraftAction,
} from 'global/constants';
import { useBatchComponentUpdateContext } from '../../shared/batch-component-update.context';

export const useMultipleChoiceUpdate = ({ id, sectionId }: SettingsProps) => {
  const { loading, updateComponentProperties } =
    useBatchComponentUpdateContext();

  const handleUpdate = useCallback(
    (
      properties: MultipleChoiceProperties,
      options?: {
        addDraftAction?: DraftAction;
        clearSuggestedAnswers?: boolean;
        isSaveImmediate?: boolean;
        onCompleted?: () => void;
      }
    ) => {
      updateComponentProperties({
        input: {
          componentId: id,
          sectionId,
          componentData: {
            properties: {
              layout: properties.layout,
              multipleChoice: {
                ...properties.multipleChoice,
                options: getOptionsPayload(properties.multipleChoice.options),
              },
            },
          },
          ...(options?.clearSuggestedAnswers
            ? {
                suggestedAnswers: [],
                suggestedAnswerType: 'example',
              }
            : undefined),
          addDraftAction: options?.addDraftAction || null,
        },
        properties,
        typename: 'ActivitiesMultipleChoiceComponent',
        isSaveImmediate: options?.isSaveImmediate || false,
        onCompleted: options?.onCompleted || undefined,
      });
    },
    [id, sectionId, updateComponentProperties]
  );

  const handleFileUpload = useCallback(
    ({
      id,
      key,
      fileName,
      properties,
      onCompleted,
    }: {
      id: string;
      key: string;
      fileName: string;
      properties: MultipleChoiceProperties;
      onCompleted?: () => void;
    }) => {
      const options = properties.multipleChoice.options;
      handleUpdate(
        {
          ...properties,
          multipleChoice: {
            ...properties.multipleChoice,
            options: options.map(opt => {
              if (id === opt.itemId) {
                return {
                  ...opt,
                  image: {
                    __typename: 'MultiChoiceOptionImage',
                    itemId: customNanoId(),
                    key,
                    alt: null,
                    fileName,
                    original: '',
                    data: null,
                  },
                };
              }
              return {
                ...opt,
                image: opt.image
                  ? {
                      __typename: 'MultiChoiceOptionImage',
                      itemId: opt.image.itemId,
                      key: opt.image.key,
                      alt: opt.image.alt,
                      fileName: opt.image.fileName,
                      original: '',
                      data: opt.image.data,
                    }
                  : null,
              };
            }),
          },
        },
        {
          addDraftAction: DRAFT_ACTION_COMPONENT_IMAGE_UPDATE,
          isSaveImmediate: true,
          onCompleted,
        }
      );
    },
    [handleUpdate]
  );

  return {
    loading,
    handleUpdate,
    handleFileUpload,
  };
};
